import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useWealthboxDomainCommand } from '../../../../../api/wealthbox'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydDatePicker from '../../../../commonDesign/SydDatePicker'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import { fetchEventCategories } from './wealthboxQueryHelpers'
import { extractWealthboxErrorMessage } from './wealthboxErrors'

const STATE_OPTIONS = [
  { label: '(None)', value: '' },
  { label: 'Unconfirmed', value: 'unconfirmed' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Tentative', value: 'tentative' }
]

const DURATION_OPTIONS = [
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '45 minutes', value: 45 },
  { label: '1 hour', value: 60 },
  { label: '90 minutes', value: 90 },
  { label: '2 hours', value: 120 }
]

const CREATE_DEFAULTS = {
  title: '',
  starts_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
  starts_time: '09:00',
  duration: 60,
  all_day: false,
  location: '',
  description: '',
  event_category: '',
  state: ''
}

function combineDateTime (date, time, allDay) {
  if (!date) return ''
  if (allDay) return dayjs(date).startOf('day').toISOString()
  return dayjs(`${date}T${time || '00:00'}`).toISOString()
}

function CreateWealthboxEventModal ({
  open,
  onClose,
  scope = 'client',
  contactId,
  formMode = 'calendar',
  invalidateQueryKeys
}) {
  const { clientId } = useAppContext()
  const isCalendar = formMode === 'calendar'
  const [categoryOptions, setCategoryOptions] = useState([])
  const [submitError, setSubmitError] = useState(null)

  const createEvent = useWealthboxDomainCommand({ invalidateQueryKeys })
  const { control, handleSubmit, watch, reset } = useForm({ defaultValues: CREATE_DEFAULTS })
  const allDay = watch('all_day')

  useEffect(() => {
    if (!open || !isCalendar) return
    let cancelled = false
    fetchEventCategories({ clientId })
      .then(opts => { if (!cancelled) setCategoryOptions(opts) })
      .catch(() => { /* non-critical */ })
    return () => { cancelled = true }
  }, [open, isCalendar, clientId])

  useEffect(() => {
    if (open) {
      reset(CREATE_DEFAULTS)
      setSubmitError(null)
    }
  }, [open, reset])

  const submit = handleSubmit(async (values) => {
    setSubmitError(null)
    // Wealthbox event body fields are snake_case (`starts_at`, `ends_at`,
    // `all_day`). We bind the computed values to camelCase locals first to
    // avoid `camelcase` lint violations on the locals, then assign onto the
    // body object using the API's actual field names.
    const startsAt = combineDateTime(values.starts_date, values.starts_time, values.all_day)
    // Wealthbox requires both starts_at AND ends_at - derive ends_at from
    // duration (clamped to end-of-day for all-day events).
    const endsAt = values.all_day
      ? dayjs(values.starts_date).endOf('day').toISOString()
      : dayjs(startsAt).add(Number(values.duration) || 60, 'minute').toISOString()

    const body = {
      title: values.title?.trim(),
      starts_at: startsAt,
      ends_at: endsAt,
      all_day: !!values.all_day
    }
    if (values.location?.trim()) body.location = values.location.trim()
    if (values.description?.trim()) body.description = values.description.trim()
    if (values.event_category !== '' && values.event_category != null) {
      body.event_category = Number(values.event_category)
    }
    if (values.state) body.state = values.state
    if (scope === 'client' && contactId != null) {
      body.linked_to = [{ id: Number(contactId), type: 'Contact' }]
    }

    try {
      await createEvent.mutateAsync({ command: 'createEvent', request: { body, scope } })
      onClose()
    } catch (err) {
      setSubmitError(extractWealthboxErrorMessage(err, 'Failed to create event'))
    }
  })

  return (
    <SydModal open={open} onClose={onClose} title='Create Event' size='xmedium'>
      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='title'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SydLabel label='Title' required>
                  <SydInput {...field} value={field.value || ''} placeholder='Event title' />
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name='starts_date'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <SydLabel label='Date' required>
                  <SydDatePicker
                    type='date'
                    dateFormat='YYYY-MM-DD'
                    value={field.value || null}
                    onChange={(d) => field.onChange(d ? dayjs(d).format('YYYY-MM-DD') : '')}
                  />
                </SydLabel>
              )}
            />
          </Grid>
          {!allDay && (
            <Grid item xs={6} sm={3}>
              <Controller
                name='starts_time'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Start Time'>
                    <SydInput type='time' {...field} value={field.value || ''} />
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          {!allDay && (
            <Grid item xs={6} sm={3}>
              <Controller
                name='duration'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Duration'>
                    <SydSelect
                      options={DURATION_OPTIONS}
                      value={field.value ?? 60}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Controller
              name='all_day'
              control={control}
              render={({ field }) => (
                <label className='flex items-center gap-2' style={{ minHeight: '54px', cursor: 'pointer' }}>
                  <SydSwitch
                    label='All day'
                    value={!!field.value}
                    onChange={(_event, newValue) => field.onChange(newValue)}
                  />
                  <span className='font-size-sm'>All-day event</span>
                </label>
              )}
            />
          </Grid>

          {isCalendar && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name='location'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Location'>
                      <SydInput {...field} value={field.value || ''} placeholder='Office, Zoom URL, etc.' />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name='state'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='State'>
                      <SydSelect
                        options={STATE_OPTIONS}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name='event_category'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Category'>
                      <SydSelect
                        options={[{ label: '(None)', value: '' }, ...categoryOptions]}
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          field.onChange(raw === '' ? '' : Number(raw))
                        }}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name='description'
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Description'>
                      <SydTextArea {...field} value={field.value || ''} rows={4} placeholder='Description' />
                    </SydLabel>
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>

        {submitError ? (
          <div className='font-size-xs mt-2' style={{ color: 'var(--color-decline-500)' }}>{submitError}</div>
        ) : null}

        <SydModalActions>
          <SydButton variant='ghost' size='sm' onClick={onClose} disabled={createEvent.isPending} type='button'>Cancel</SydButton>
          <SydButton variant='primary' size='sm' processing={createEvent.isPending} type='submit'>Create Event</SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

CreateWealthboxEventModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(['client', 'user', 'all']),
  contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formMode: PropTypes.oneOf(['simple', 'calendar']),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

export default CreateWealthboxEventModal
